/**
 * Чужая свежая правка (§4.3): после «Обновить» ячейки, которые переехали у
 * других, 10 секунд несут подчёркивание 2 px `--fresh` — видно, что именно
 * изменилось. Таблица сама данные не подменяет, поэтому отсчёт начинается в
 * момент прихода новой ревизии в стор.
 */
import { useEffect, useRef, useState } from 'react'
import type { Brand, ChannelCode } from '@shared/types.ts'

const FRESH_MS = 10_000

const CHANNEL_FIELDS = [
  'visibility', 'tier', 'url', 'stockDisplay', 'oos',
  'delivery', 'installation', 'warranty', 'seoPriority', 'contextPriority',
] as const

export const freshKey = (brandId: number, channel: ChannelCode, field: string) =>
  `${brandId}:${channel}:${field}`

function snapshot(brands: readonly Brand[]): Map<string, unknown> {
  const map = new Map<string, unknown>()
  for (const brand of brands) {
    map.set(`${brand.id}::name`, brand.name)
    for (const [code, values] of Object.entries(brand.channels)) {
      if (!values) continue
      const channel = code as ChannelCode
      for (const field of CHANNEL_FIELDS) {
        map.set(freshKey(brand.id, channel, field), values[field])
      }
    }
  }
  return map
}

/** Ключи ячеек, изменившихся чужими пакетами с прошлой ревизии. */
export function useFreshCells(brands: readonly Brand[], revision: number): ReadonlySet<string> {
  const previous = useRef<{ revision: number; values: Map<string, unknown> } | null>(null)
  const [fresh, setFresh] = useState<ReadonlySet<string>>(() => new Set<string>())

  useEffect(() => {
    const next = snapshot(brands)
    const before = previous.current
    previous.current = { revision, values: next }

    if (!before || before.revision === revision) return

    const changed = new Set<string>()
    for (const [key, value] of next) {
      if (before.values.has(key) && before.values.get(key) !== value) changed.add(key)
    }
    if (changed.size === 0) return

    setFresh(changed)
    const timer = setTimeout(() => setFresh(new Set<string>()), FRESH_MS)
    return () => clearTimeout(timer)
  }, [brands, revision])

  return fresh
}
